// ============================================================
// attack/swing - handleAttack: the main attack flow.
//
// Validation, countdown, ammo, dread, skill, then the roll and what it
// means: auto-hit, jam, overheat, miss (with its re-roll prompt and a
// missed blast's scatter), or a hit handed to `attack/shot`.
//
// ROLL ORDER CONTRACT: the attack d100 (a Charmed target's re-roll of a
// hit right after it), Reliable's confirmation d100 on a 00, then - on a
// scattering miss - the scatter dice, then whatever the shot draws.
// ============================================================
import { helplessAgainst } from '../../engine/helpless';
import { areaOf, expectedArea } from '../../engine/areaShapes';
import { attackGate } from '../../engine/exposures';
import { rollD100 } from '../../engine/index';
import type { Tab } from '../../types';
import { armSwing, hitFrom, type Swing } from './armed';
import { actorReader, aimedBearer, type AttackContext } from './context';
import { checkOverheat, isJammed, writeJam } from './misfire';
import { missSwing } from './miss';
import { settleAttack } from './outcome';
import { resumeAttackFromHit } from './shot';
import { autoHitNote, swingNotes, type SwingFlags } from './swingNotes';

export function promptOpen(ctx: AttackContext): boolean {
  return !!(ctx.pendingAttack || ctx.pendingReroll || ctx.pendingBonusAttack);
}

/** Why the attack is refused with a line in the log, or null. A ram asks the same (attack/ram). */
export function refusalReason(ctx: AttackContext): string | null {
  // A reason the shot cannot be taken at all - today only Indirect's
  // minimum range (v8.198.5); the field is deliberately generic.
  if (ctx.attackBlockedReason) return '🔴 ' + ctx.attackBlockedReason;
  if (ctx.sessionId && ctx.isDefencePending && ctx.isDefencePending(ctx.activeTab)) {
    return 'Cannot attack - defence pending on this target.';
  }
  return null;
}

/** Nothing to attack with, a prompt still open, a lock, or a stated refusal. */
function attackRefused(ctx: AttackContext): boolean {
  if (ctx.selectedSlot === null || !ctx.currentTab || promptOpen(ctx) || ctx.isLocked('attack')) return true;
  const reason = refusalReason(ctx);
  if (reason) ctx.addNarrative(ctx.activeTab, { type: 'info', text: reason, timestamp: Date.now() });
  return reason !== null;
}

/**
 * Phase 7: the area block's auto-hit (Spray's preset, or a typed block that
 * says so, v8.369.1) - or an `autoHit` exposure on the target (v8.368.2) -
 * bypasses the d100 and the jam entirely. Returns whether it did.
 */
function tryAutoHit(ctx: AttackContext, sw: Swing): boolean {
  const gate = attackGate(aimedBearer(ctx), actorReader(ctx, sw.isMelee));
  const area = expectedArea(areaOf(sw.slot), sw.sp);
  if (gate.autoHit && !area.autoHit) sw.notes.push('Exposed: the attack cannot miss.');
  if (!area.autoHit && !gate.autoHit) return false;
  if (area.autoHit) sw.notes.push(autoHitNote(sw.slot, sw.sp, area));
  resumeAttackFromHit(ctx, hitFrom(ctx, sw, 0));
  ctx.historyTrack.current = true;
  return true;
}

/**
 * Roll the d100. A Charmed target (luck < 0, v8.368.5) forces a hit to be
 * rolled again. Aim (and, for melee, a won feint) is spent by the attack
 * itself, whatever the die says; specialRules ride along so the component
 * can start a Cycling cooldown (v8.289.0).
 */
function rollAttack(ctx: AttackContext, sw: Swing): number {
  let attackRoll = rollD100();
  if (sw.onHit.luck < 0 && attackRoll <= sw.modifiedSkill) {
    const again = rollD100();
    sw.notes.push(`Charmed: the hit is rolled again (${attackRoll} → ${again}).`);
    attackRoll = again;
  }
  ctx.onAttackRolled?.({ isMelee: sw.isMelee, specialRules: sw.sp });
  sw.notes.push(...swingNotes(swingFlags(ctx, sw.isMelee)));
  return attackRoll;
}

/** What the talent and combat-action lines read. */
function swingFlags(ctx: AttackContext, isMelee: boolean): SwingFlags {
  return {
    isMelee,
    rangeBand: ctx.rangeBand,
    removeLongRangePenalty: !!ctx.attackerCharStats?.removeLongRangePenalty,
    removeExtremeRangePenalty: !!ctx.attackerCharStats?.removeExtremeRangePenalty,
    isCalledShot: ctx.isCalledShot,
    calledShotReduction: ctx.calledShotReduction,
    calledShotIgnored: ctx.calledShotIgnored,
    isAllOutAttack: ctx.isAllOutAttack,
    isGuardedAttack: ctx.isGuardedAttack,
    isCharge: ctx.isCharge,
    hasBerserkCharge: ctx.hasBerserkCharge,
    hasHammerBlow: ctx.hasHammerBlow,
    isGangUp: ctx.isGangUp,
    frenzied: ctx.frenzied,
  };
}

/**
 * A HELPLESS target cannot be missed (engine/helpless). The d100 was rolled
 * and has already had its chance to jam or overheat; a failed roll goes on
 * as it is, so the hit it becomes carries no degrees of success - one bare
 * hit. Says so in the log, and returns whether it applied.
 */
function landsOnHelpless(ctx: AttackContext, sw: Swing, attackRoll: number): boolean {
  if (attackRoll <= sw.modifiedSkill || !helplessAgainst(sw.tab.target, sw.isMelee, ctx.condModOptions)) return false;
  sw.notes.push('Helpless: the attack cannot miss - one bare hit.');
  return true;
}

/**
 * The swing after it is armed: auto-hit, or the roll and where it leads.
 * Returns how it came out: a hit, a miss, a jam (the weapon did not go off),
 * or `pending` - a miss waiting on a re-roll prompt, not settled yet.
 */
function resolveSwing(ctx: AttackContext, tab: Tab, sw: Swing): 'hit' | 'miss' | 'jam' | 'pending' {
  if (tryAutoHit(ctx, sw)) return 'hit';
  const attackRoll = rollAttack(ctx, sw);
  const isJam = isJammed(ctx, sw, attackRoll);
  checkOverheat(ctx, sw, attackRoll);
  if (isJam) {
    writeJam(ctx, sw, attackRoll);
    return 'jam';
  }
  const success = attackRoll <= sw.modifiedSkill || landsOnHelpless(ctx, sw, attackRoll);
  if (!success) return missSwing(ctx, sw, attackRoll) ? 'pending' : 'miss';
  // A HIT touches the exposed target in either mode (v8.353.0, v8.368.4).
  ctx.onTouched?.({ target: tab.target, outcome: 'hit', isMelee: sw.isMelee });
  resumeAttackFromHit(ctx, hitFrom(ctx, sw, attackRoll));
  return 'hit';
}

/** From the countdown to the outcome - see the file header. */
function swing(ctx: AttackContext, tab: Tab, slotIndex: number): void {
  // The FREE toggle is gone (v8.218.0): the roundCountdown house rule and
  // the turn tracker already decide when this runs.
  const cdResult = ctx.computeCountdown(ctx.activeTab);
  const sw = armSwing(ctx, tab, slotIndex, cdResult);
  if (!sw) return;
  const outcome = resolveSwing(ctx, tab, sw);
  // A miss waiting on a re-roll is settled when the prompt is answered (attack/prompts): until
  // then the target is still unaware, and nobody has been told how the attack came out.
  if (outcome === 'pending') return;
  // A jam alerts the target too (they heard it), but nothing is reported: the weapon did not go off.
  settleAttack(ctx, tab.id, outcome === 'jam' ? null : { hit: outcome === 'hit', slot: sw.slot, isMelee: sw.isMelee });
}

/** handleAttack: refuse, or swing - an error anywhere lands in the log, never lost. */
export function handleAttack(ctx: AttackContext): void {
  if (attackRefused(ctx)) return;
  try {
    swing(ctx, ctx.currentTab as Tab, ctx.selectedSlot as number);
  } catch (err) {
    ctx.addNarrative(ctx.activeTab, {
      type: 'info',
      text: '\u26A0 Error: ' + (err instanceof Error ? err.message : String(err)),
      timestamp: Date.now(),
    });
  }
}
